import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { mergeReducedMotionPreset, useThemeMotion } from '../../animation/themeMotion'
import type { LibrarySidebarTreeModel } from '../../lib/folderTree'
import { isUnderReferenceUploadSubtreeUiPath } from '../../lib/libraryPaths'
import { ChromePrimaryButton } from '../ChromeActions'
import { chromeIconButtonClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { IconFolderPlus } from '../nav-icons'
import { LibraryFolderPathBreadcrumb } from './LibraryFolderPathBreadcrumb'
import { LibraryFolderTreeView } from './LibraryFolderTreeView'

type Props = {
  open: boolean
  onClose: () => void
  model: LibrarySidebarTreeModel
  selectedPath: string
  onSelect: (path: string) => void
  angular: boolean
  /** 传入当前草稿路径作新建父级（与抽屉内选中一致，勿等点「确定」同步） */
  onRequestNewFolder?: (draftParentPath: string) => void
}

/**
 * 移动端从底部抽屉展开文件夹树，与桌面侧栏语义一致。
 * 入场动效对齐 {@link ThemeModal}（窄屏：`opacity 0→1` + `y: 100% → 0`，遮罩自 0.88 淡入）。
 */
export function LibraryFolderTreeDrawer({
  open,
  onClose,
  model,
  selectedPath,
  onSelect,
  angular,
  onRequestNewFolder,
}: Props) {
  const [draftPath, setDraftPath] = useState(selectedPath)
  const newFolderDisabled = isUnderReferenceUploadSubtreeUiPath(draftPath)

  useEffect(() => {
    if (open) setDraftPath(selectedPath)
  }, [open, selectedPath])

  const confirmSelection = () => {
    onSelect(draftPath)
    onClose()
  }

  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)
  const overlayDur = Math.min(0.32, Math.max(0.2, preset.duration * 0.55))
  const panelDur = Math.min(0.42, Math.max(0.24, preset.expandDuration * 0.72))
  const ease = [...preset.easing] as [number, number, number, number]

  const reduced = !!prefersReduced
  const backdropTransition = reduced
    ? { duration: 0.16, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: Math.min(0.24, overlayDur), ease }

  const panelTransition = reduced
    ? { duration: 0.18, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: panelDur, ease }

  const panelHidden = reduced ? { opacity: 0 } : { opacity: 0, y: '100%' }
  const panelVisible = { opacity: 1, y: 0 }

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (!open) return
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open ? (
        <div
          key="library-folder-drawer"
          className="fixed inset-0 z-[170] flex flex-col justify-end lg:hidden"
          role="presentation"
        >
          <motion.button
            type="button"
            aria-label="关闭文件夹"
            className="theme-modal-backdrop absolute inset-0 cursor-default"
            initial={{ opacity: 0.88 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={backdropTransition}
            style={
              reduced
                ? { backdropFilter: 'none', WebkitBackdropFilter: 'none' }
                : undefined
            }
            onClick={onClose}
          />
          <motion.div
            className={[
              'relative z-[1] flex h-[60dvh] flex-col border-t border-line bg-panel shadow-[0_-8px_32px_color-mix(in_srgb,var(--ink)_28%,transparent)]',
              angular ? 'rounded-none' : 'rounded-t-2xl',
            ].join(' ')}
            style={{ transformOrigin: 'bottom center' }}
            initial={panelHidden}
            animate={panelVisible}
            exit={panelHidden}
            transition={panelTransition}
          >
            <div className="flex shrink-0 flex-col gap-2 border-b border-line pb-3 pl-4 pr-[max(0.5rem,env(safe-area-inset-right))] pt-3">
              <span className="typo-lib-section text-ink">文件夹</span>
              <div className="flex min-w-0 items-center gap-1">
                <div className="min-w-0 flex-1">
                  <LibraryFolderPathBreadcrumb selectedFolderPath={draftPath} onNavigate={setDraftPath} dense />
                </div>
                {onRequestNewFolder ? (
                  <ChromeTooltip
                    content={
                      newFolderDisabled ? '本地参考图目录下不可新建子文件夹' : '新建文件夹'
                    }
                    angular={angular}
                    side="bottom"
                    align="end"
                  >
                    <button
                      type="button"
                      disabled={newFolderDisabled}
                      aria-label="新建文件夹"
                      onClick={() => onRequestNewFolder(draftPath)}
                      className={[chromeIconButtonClass(angular), '-mr-px shrink-0'].join(' ')}
                    >
                      <IconFolderPlus className="h-5 w-5 shrink-0" aria-hidden />
                    </button>
                  </ChromeTooltip>
                ) : null}
              </div>
            </div>
            <div className="scroll-region flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-3 py-2 pb-2 pe-3 sm:pe-4">
              <LibraryFolderTreeView
                model={model}
                selectedPath={draftPath}
                onSelect={setDraftPath}
                angular={angular}
                showLibraryRootRow
                fillRemainingHeight
              />
            </div>
            <div className="flex shrink-0 gap-2 border-t border-line px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
              <ChromePrimaryButton type="button" className="min-h-10 w-full" onClick={confirmSelection}>
                确定
              </ChromePrimaryButton>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  )
}
